if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentSlide = 0;
        this.slides = [];
        this.thumbnails = [];
        this.initializeCarousel();
      }

      connectedCallback() {
        super.connectedCallback && super.connectedCallback();
        this.setupCarouselEventListeners();
        
        // Override the media modal close behavior after the element is connected
        this.overrideModalCloseHandlers();
      }

      overrideModalCloseHandlers() {
        // Remove existing event listeners by replacing them with our custom logic
        this.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse') {
            // Check if clicking on close button
            const closeButton = this.querySelector('[id^="ModalClose-"]');
            if (event.target === closeButton || closeButton?.contains(event.target)) {
              this.hide();
              return;
            }
            
            // Check if clicking inside carousel content
            const carouselWrapper = this.querySelector('.product-media-modal__carousel-wrapper');
            if (carouselWrapper && carouselWrapper.contains(event.target)) {
              // Don't close modal when clicking carousel content
              return;
            }
            
            // Close modal when clicking outside carousel content
            if (event.target === this || (!carouselWrapper && !event.target.closest('deferred-media, product-model'))) {
              this.hide();
            }
          }
        });

        // Handle touch events for mobile
        this.addEventListener('touchend', (event) => {
          // Check if touching on close button
          const closeButton = this.querySelector('[id^="ModalClose-"]');
          if (event.target === closeButton || closeButton?.contains(event.target)) {
            this.hide();
            return;
          }
          
          // Check if touching inside carousel content
          const carouselWrapper = this.querySelector('.product-media-modal__carousel-wrapper');
          if (carouselWrapper && carouselWrapper.contains(event.target)) {
            // Don't close modal when touching carousel content
            return;
          }
          
          // Close modal when touching outside carousel content
          if (event.target === this || (!carouselWrapper && !event.target.closest('deferred-media, product-model'))) {
            this.hide();
          }
        });
      }

      initializeCarousel() {
        // Wait for DOM to be ready
        setTimeout(() => {
          this.slides = this.querySelectorAll('.carousel__slide');
          this.thumbnails = this.querySelectorAll('.thumbnail__item');
          this.setupCarouselControls();
          this.loadAllMedia();
          this.updateNavigationVisibility();
        }, 100);
      }

      updateNavigationVisibility() {
        const prevButton = this.querySelector('.carousel__nav--prev');
        const nextButton = this.querySelector('.carousel__nav--next');
        const thumbnailsContainer = this.querySelector('.product-media-modal__thumbnails');
        
        // Hide navigation if only one slide
        if (this.slides.length <= 1) {
          if (prevButton) prevButton.style.display = 'none';
          if (nextButton) nextButton.style.display = 'none';
          if (thumbnailsContainer) thumbnailsContainer.style.display = 'none';
        } else {
          if (prevButton) prevButton.style.display = 'flex';
          if (nextButton) nextButton.style.display = 'flex';
          if (thumbnailsContainer) thumbnailsContainer.style.display = 'block';
        }
      }

      loadAllMedia() {
        // Load all deferred media in the carousel
        this.slides.forEach((slide, index) => {
          const deferredMedia = slide.querySelector('deferred-media');
          if (deferredMedia) {
            // Load all slides immediately since they're in a carousel
            this.loadMediaContent(deferredMedia);
          }
          
          // Also check for any templates that might contain media
          const templates = slide.querySelectorAll('template');
          templates.forEach(template => {
            if (template.content) {
              const mediaElement = template.content.querySelector('img, video, iframe, model-viewer');
              if (mediaElement && !slide.querySelector('img, video, iframe, model-viewer')) {
                // Clone and insert the media element
                const clonedMedia = mediaElement.cloneNode(true);
                const container = slide.querySelector('.product-media-modal__content');
                if (container) {
                  container.appendChild(clonedMedia);
                }
              }
            }
          });
        });
      }

      loadMediaContent(deferredMedia) {
        if (deferredMedia.getAttribute('loaded')) return;
        
        // Try the deferred-media custom element's loadContent method first
        if (typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false); // false = don't focus
          deferredMedia.setAttribute('loaded', true);
          return;
        }
        
        // Fallback to manual template processing
        const template = deferredMedia.querySelector('template');
        if (template && template.content) {
          const content = document.createElement('div');
          content.appendChild(template.content.firstElementChild.cloneNode(true));
          
          deferredMedia.setAttribute('loaded', true);
          const mediaElement = content.querySelector('img, video, model-viewer, iframe');
          
          if (mediaElement) {
            // Remove any existing poster or placeholder
            const poster = deferredMedia.querySelector('[id^="Deferred-Poster-"]');
            if (poster) {
              poster.style.display = 'none';
            }
            
            deferredMedia.appendChild(mediaElement);
            
            // Handle video autoplay
            if (mediaElement.nodeName === 'VIDEO' && mediaElement.getAttribute('autoplay')) {
              mediaElement.play().catch(() => {
                // Autoplay failed, which is fine
              });
            }
            
            // Force style update for Safari iframe bug workaround
            if (mediaElement.nodeName === 'IFRAME') {
              const formerStyle = mediaElement.getAttribute('style');
              mediaElement.setAttribute('style', 'display: block;');
              setTimeout(() => {
                mediaElement.setAttribute('style', formerStyle || '');
              }, 0);
            }
          }
        }
      }

      setupCarouselControls() {
        const prevButton = this.querySelector('.carousel__nav--prev');
        const nextButton = this.querySelector('.carousel__nav--next');
        
        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.prevSlide();
          });
          
          // Add touch support for navigation buttons
          prevButton.addEventListener('touchend', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.prevSlide();
          });
        }
        
        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.nextSlide();
          });
          
          // Add touch support for navigation buttons
          nextButton.addEventListener('touchend', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.nextSlide();
          });
        }

        // Setup thumbnail clicks
        this.thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', (e) => {
            e.stopPropagation();
            this.goToSlide(index);
          });
          
          // Add touch support for thumbnails
          thumb.addEventListener('touchend', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.goToSlide(index);
          });
        });

        // Setup keyboard navigation
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') {
            this.nextSlide();
          } else if (e.key === 'ArrowLeft') {
            this.prevSlide();
          }
        });

        // Setup touch/swipe
        this.setupSwipeGestures();
      }

      setupSwipeGestures() {
        const carousel = this.querySelector('.product-media-modal__carousel');
        if (!carousel) return;

        let touchStartX = 0;
        let touchEndX = 0;
        let touchStartY = 0;
        let touchEndY = 0;

        carousel.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
          touchStartY = e.changedTouches[0].screenY;
        }, { passive: true });

        carousel.addEventListener('touchend', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          touchEndY = e.changedTouches[0].screenY;
          this.handleSwipe();
        }, { passive: true });

        this.handleSwipe = () => {
          const swipeThreshold = 50;
          const swipeDistanceX = touchEndX - touchStartX;
          const swipeDistanceY = touchEndY - touchStartY;
          
          // Only handle horizontal swipes (ignore vertical scrolling)
          if (Math.abs(swipeDistanceX) > Math.abs(swipeDistanceY) && Math.abs(swipeDistanceX) > swipeThreshold) {
            if (swipeDistanceX > 0) {
              this.prevSlide();
            } else {
              this.nextSlide();
            }
          }
        };
      }

      setupCarouselEventListeners() {
        // Prevent event bubbling from carousel content
        const carouselWrapper = this.querySelector('.product-media-modal__carousel-wrapper');
        if (carouselWrapper) {
          carouselWrapper.addEventListener('click', (e) => {
            e.stopPropagation();
          });
          
          carouselWrapper.addEventListener('pointerup', (e) => {
            e.stopPropagation();
          });
          
          carouselWrapper.addEventListener('touchend', (e) => {
            e.stopPropagation();
          });
        }
      }

      goToSlide(index) {
        if (index < 0 || index >= this.slides.length) return;
        
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('carousel__slide--active', i === index);
        });
        
        this.thumbnails.forEach((thumb, i) => {
          thumb.classList.toggle('thumbnail__item--active', i === index);
        });
        
        this.currentSlide = index;

        // Load media for the current slide if not already loaded
        const currentSlide = this.slides[index];
        if (currentSlide) {
          const deferredMedia = currentSlide.querySelector('deferred-media');
          if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
            this.loadMediaContent(deferredMedia);
          }
        }
      }

      nextSlide() {
        const nextIndex = this.currentSlide >= this.slides.length - 1 ? 0 : this.currentSlide + 1;
        this.goToSlide(nextIndex);
      }

      prevSlide() {
        const prevIndex = this.currentSlide <= 0 ? this.slides.length - 1 : this.currentSlide - 1;
        this.goToSlide(prevIndex);
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        
        // Reinitialize carousel after modal is shown
        setTimeout(() => {
          this.initializeCarousel();
        }, 100);
      }

      showActiveMedia() {
        // Find the media that corresponds to the clicked thumbnail
        const mediaId = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
        
        if (mediaId) {
          // Find the slide index that matches this media ID
          const targetSlide = Array.from(this.slides).findIndex(slide => 
            slide.getAttribute('data-media-id') === mediaId
          );
          
          if (targetSlide !== -1) {
            this.goToSlide(targetSlide);
            return;
          }
        }

        // Fallback to original behavior - but also ensure media loading
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy?.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy?.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;
        
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        if (container) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }

        // Also load deferred media in carousel slides
        const deferredMedia = activeMedia.querySelector('deferred-media');
        if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
          this.loadMediaContent(deferredMedia);
        }
      }
    }
  );
}